import React from 'react';
import { RotateCw, RotateCcw, FlipHorizontal2, FlipVertical2, ZoomIn, Sun, Contrast, Droplets, Undo2 } from 'lucide-react';

export const ASPECTS = [
  { key: 'free', label: 'Free', value: undefined },
  { key: '1', label: '1:1', value: 1 },
  { key: '43', label: '4:3', value: 4 / 3 },
  { key: '32', label: '3:2', value: 3 / 2 },
  { key: '169', label: '16:9', value: 16 / 9 },
  { key: '34', label: '3:4', value: 3 / 4 },
  { key: '916', label: '9:16', value: 9 / 16 },
];

export const DEFAULT_ADJUST = { brightness: 100, contrast: 100, saturate: 100, grayscale: 0, sepia: 0 };

export const PRESETS = [
  { key: 'none', label: 'None', values: {} },
  { key: 'bw', label: 'Black & white', values: { grayscale: 100 } },
  { key: 'sepia', label: 'Sepia', values: { sepia: 80, contrast: 105 } },
  { key: 'vivid', label: 'Vivid', values: { saturate: 135, contrast: 110 } },
  { key: 'soft', label: 'Soft', values: { contrast: 90, brightness: 105, saturate: 90 } },
];

export const OUTPUT_SIZES = [
  { value: 0, label: 'Original size' },
  { value: 2048, label: 'Large (2048px)' },
  { value: 1600, label: 'Medium (1600px)' },
  { value: 1200, label: 'Web (1200px)' },
  { value: 800, label: 'Small (800px)' },
];

const btn = (active) => (active
  ? { background: 'var(--maroon)', color: 'var(--cream)' }
  : { background: 'white', border: '1px solid rgba(0,0,0,0.15)', color: 'var(--navy)' });

export function ToolButton({ onClick, title, testId, active, children }) {
  return (
    <button type="button" onClick={onClick} title={title} aria-label={title} data-testid={testId}
      className="flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-title" style={btn(active)}>
      {children}
    </button>
  );
}

export function Slider({ icon: Icon, label, value, min, max, step = 1, onChange, testId, unit = '' }) {
  return (
    <div className="flex items-center gap-2">
      <Icon size={16} style={{ color: 'var(--olive)' }} title={label} />
      <input type="range" min={min} max={max} step={step} value={value} data-testid={testId} aria-label={label}
        onChange={(e) => onChange(Number(e.target.value))} className="flex-1 accent-[var(--maroon)]" />
      <span className="text-xs w-12 text-right tabular-nums" style={{ color: 'var(--olive)' }}>{value}{unit}</span>
    </div>
  );
}

export function CropTools({ aspectKey, setAspectKey, zoom, setZoom, rotation, setRotation, flipX, setFlipX, flipY, setFlipY }) {
  return (
    <div className="space-y-3" data-testid="photo-edit-crop-tools">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-xs font-title mr-1" style={{ color: 'var(--olive)' }}>Shape</span>
        {ASPECTS.map((a) => (
          <ToolButton key={a.key} onClick={() => setAspectKey(a.key)} testId={`photo-edit-aspect-${a.key}`} active={aspectKey === a.key} title={a.label}>{a.label}</ToolButton>
        ))}
      </div>
      <Slider icon={ZoomIn} label="Zoom" value={Number(zoom.toFixed(2))} min={1} max={4} step={0.01} onChange={setZoom} testId="photo-edit-zoom" unit="×" />
      <Slider icon={RotateCw} label="Rotate" value={rotation} min={0} max={359} onChange={setRotation} testId="photo-edit-rotate" unit="°" />
      <div className="flex items-center gap-1.5 flex-wrap">
        <ToolButton onClick={() => setRotation((r) => (r + 270) % 360)} title="Rotate left 90°" testId="photo-edit-rotate-left"><RotateCcw size={14} /> Left</ToolButton>
        <ToolButton onClick={() => setRotation((r) => (r + 90) % 360)} title="Rotate right 90°" testId="photo-edit-rotate-90"><RotateCw size={14} /> Right</ToolButton>
        <ToolButton onClick={() => setFlipX((f) => !f)} title="Flip horizontally" testId="photo-edit-flip-h" active={flipX}><FlipHorizontal2 size={14} /> Flip H</ToolButton>
        <ToolButton onClick={() => setFlipY((f) => !f)} title="Flip vertically" testId="photo-edit-flip-v" active={flipY}><FlipVertical2 size={14} /> Flip V</ToolButton>
        <ToolButton onClick={() => setRotation(0)} title="Straighten (reset rotation)" testId="photo-edit-rotate-reset"><Undo2 size={14} /> 0°</ToolButton>
      </div>
    </div>
  );
}

export function AdjustTools({ adjust, setAdjust }) {
  const set = (k) => (v) => setAdjust((a) => ({ ...a, [k]: v }));
  const presetKey = PRESETS.find((p) => Object.entries({ ...DEFAULT_ADJUST, ...p.values }).every(([k, v]) => adjust[k] === v))?.key;
  return (
    <div className="space-y-3" data-testid="photo-edit-adjust-tools">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-xs font-title mr-1" style={{ color: 'var(--olive)' }}>Preset</span>
        {PRESETS.map((p) => (
          <ToolButton key={p.key} onClick={() => setAdjust({ ...DEFAULT_ADJUST, ...p.values })} testId={`photo-edit-preset-${p.key}`} active={presetKey === p.key} title={p.label}>{p.label}</ToolButton>
        ))}
      </div>
      <Slider icon={Sun} label="Brightness" value={adjust.brightness} min={40} max={160} onChange={set('brightness')} testId="photo-edit-brightness" unit="%" />
      <Slider icon={Contrast} label="Contrast" value={adjust.contrast} min={40} max={160} onChange={set('contrast')} testId="photo-edit-contrast" unit="%" />
      <Slider icon={Droplets} label="Saturation" value={adjust.saturate} min={0} max={200} onChange={set('saturate')} testId="photo-edit-saturation" unit="%" />
      <div className="flex justify-end">
        <ToolButton onClick={() => setAdjust(DEFAULT_ADJUST)} title="Reset adjustments" testId="photo-edit-adjust-reset"><Undo2 size={14} /> Reset adjustments</ToolButton>
      </div>
    </div>
  );
}
